import React from 'react';

// Renders the lightweight markdown JARVIS emits (headings, bold, bullets,
// dividers) as cleanly styled React — no raw #, *, or --- shown to the user.

// Inline formatting: **bold**, *italic*, `code`.
function renderInline(text, keyPrefix) {
  const out = [];
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] !== undefined) {
      out.push(<strong key={`${keyPrefix}-b${i}`} style={{ color: 'var(--j1)', fontWeight: 700 }}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      out.push(<em key={`${keyPrefix}-i${i}`} style={{ color: 'var(--txt)' }}>{m[3]}</em>);
    } else if (m[4] !== undefined) {
      out.push(
        <code key={`${keyPrefix}-c${i}`} style={{ fontFamily: 'Share Tech Mono', fontSize: '0.92em', background: 'rgba(0,212,255,0.08)', border: '1px solid var(--brd2)', borderRadius: 3, padding: '1px 4px', color: 'var(--j1)' }}>
          {m[4]}
        </code>
      );
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function MessageContent({ content }) {
  const lines = (content || '').split('\n');
  const blocks = [];
  let bullets = null;

  const flushBullets = () => {
    if (bullets && bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} style={{ margin: '4px 0', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--j1)', flexShrink: 0, lineHeight: 1.5 }}>▸</span>
              <span>{renderInline(b, `li-${blocks.length}-${i}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    bullets = null;
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const trimmed = line.trim();

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushBullets();
      blocks.push(<div key={`hr-${idx}`} style={{ height: 1, background: 'linear-gradient(90deg,transparent,var(--brd),transparent)', margin: '8px 0' }} />);
      return;
    }

    // Blank line
    if (trimmed === '') {
      flushBullets();
      blocks.push(<div key={`sp-${idx}`} style={{ height: 4 }} />);
      return;
    }

    // Headings (#, ##, ###)
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushBullets();
      const level = h[1].length;
      const size = level <= 1 ? 15 : level === 2 ? 13.5 : 12.5;
      blocks.push(
        <div key={`h-${idx}`} style={{ fontFamily: 'Orbitron', fontWeight: 700, fontSize: size, color: 'var(--j1)', letterSpacing: 0.5, margin: '8px 0 2px', textShadow: '0 0 10px var(--glow1)' }}>
          {renderInline(h[2], `h-${idx}`)}
        </div>
      );
      return;
    }

    // Bullet list item (-, *, •)
    const bullet = trimmed.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      if (!bullets) bullets = [];
      bullets.push(bullet[1]);
      return;
    }

    // Paragraph
    flushBullets();
    blocks.push(
      <div key={`p-${idx}`} style={{ lineHeight: 1.5 }}>
        {renderInline(trimmed, `p-${idx}`)}
      </div>
    );
  });

  flushBullets();
  return <div style={{ display: 'flex', flexDirection: 'column' }}>{blocks}</div>;
}

// Plain-text version for speech synthesis so JARVIS never reads "hashtag" or
// "asterisk" aloud.
export function stripMarkdown(s) {
  return (s || '')
    .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, '. ') // dividers -> pause
    .replace(/^#{1,6}\s+/gm, '')                      // heading markers
    .replace(/^\s*[-*•]\s+/gm, '')                    // bullet markers
    .replace(/\*\*([^*]+)\*\*/g, '$1')                // bold
    .replace(/\*([^*]+)\*/g, '$1')                    // italic
    .replace(/`([^`]+)`/g, '$1')                       // inline code
    .replace(/\n{2,}/g, '. ')                          // paragraph breaks -> pause
    .replace(/\n/g, ', ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,])/g, '$1')
    .trim();
}
